<template>
  <div class="auth-page auth-page--register">
    <header class="auth-card__header auth-card__header--newpwd">
      <h1 class="auth-card__title auth-card__title--newpwd">Nouveau mot de passe</h1>
      <p class="auth-card__subtitle auth-card__subtitle--newpwd">
        Choisissez un mot de passe sécurisé.
      </p>
    </header>
    <div class="auth-page__wrap">
      <Transition name="card" appear>
        <div class="auth-card auth-card--newpwd">
          <form v-if="!successMessage && !tokenInvalid" class="auth-form" @submit.prevent="submitReset">
            <div class="auth-form__group">
              <label for="newPassword" class="auth-form__label">Mot de passe</label>
              <div class="auth-form__input-wrap">
                <LockClosedIcon class="auth-form__icon" />
                <input
                  id="newPassword"
                  v-model="newPassword"
                  :type="showPassword ? 'text' : 'password'"
                  class="auth-form__input auth-form__input--with-btn"
                  required
                  placeholder="••••••••"
                  autocomplete="new-password"
                  @input="validatePassword"
                />
                <button
                  type="button"
                  class="auth-form__toggle-pwd"
                  :aria-label="showPassword ? 'Masquer' : 'Afficher'"
                  @click="showPassword = !showPassword"
                >
                  <EyeIcon v-if="!showPassword" class="w-5 h-5" />
                  <EyeSlashIcon v-else class="w-5 h-5" />
                </button>
              </div>
              <div v-if="newPassword" class="auth-form__strength">
                <div
                  v-for="(label, key) in strengthLabels"
                  :key="key"
                  class="auth-form__strength-item"
                  :class="{ 'auth-form__strength-item--ok': passwordChecks[key] }"
                >
                  <CheckIcon v-if="passwordChecks[key]" class="w-4 h-4" />
                  <span v-else class="auth-form__strength-dot" />
                  {{ label }}
                </div>
              </div>
            </div>

            <div class="auth-form__group">
              <label for="confirmPassword" class="auth-form__label">Confirmer le mot de passe</label>
              <div class="auth-form__input-wrap">
                <LockClosedIcon class="auth-form__icon" />
                <input
                  id="confirmPassword"
                  v-model="confirmPassword"
                  :type="showConfirm ? 'text' : 'password'"
                  class="auth-form__input auth-form__input--with-btn"
                  required
                  placeholder="••••••••"
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  class="auth-form__toggle-pwd"
                  :aria-label="showConfirm ? 'Masquer' : 'Afficher'"
                  @click="showConfirm = !showConfirm"
                >
                  <EyeIcon v-if="!showConfirm" class="w-5 h-5" />
                  <EyeSlashIcon v-else class="w-5 h-5" />
                </button>
              </div>
            </div>

            <div v-if="errorMessage" class="auth-form__error">
              <ExclamationCircleIcon class="w-5 h-5 shrink-0" />
              <span>{{ errorMessage }}</span>
            </div>

            <button type="submit" class="auth-form__submit" :disabled="loading || !isPasswordStrong">
              <span v-if="!loading" class="auth-form__submit-text">Valider</span>
              <span v-else class="auth-form__spinner" aria-hidden="true" />
            </button>
          </form>

          <Transition name="fade" mode="out-in">
            <div v-if="tokenInvalid" key="invalid" class="auth-form__error auth-form__error--block">
              <ExclamationCircleIcon class="w-5 h-5 shrink-0" />
              <span>{{ errorMessage || 'Ce lien de réinitialisation est invalide ou a expiré.' }}</span>
            </div>
            <div v-else-if="successMessage" key="success" class="auth-form__success">
              <CheckCircleIcon class="auth-form__success-icon w-14 h-14 text-emerald-500" />
              <p class="auth-form__success-msg">Mot de passe réinitialisé avec succès.</p>
              <p class="auth-form__success-hint">Redirection vers la page de connexion…</p>
            </div>
          </Transition>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  CheckIcon,
} from '@heroicons/vue/24/outline';
import axios from 'axios';
import { safeErrorMessage } from '@/utils/errorMessages';

const route = useRoute();
const router = useRouter();
const newPassword = ref('');
const confirmPassword = ref('');
const showPassword = ref(false);
const showConfirm = ref(false);
const loading = ref(false);
const tokenInvalid = ref(false);
const successMessage = ref('');
const errorMessage = ref('');
const passwordChecks = ref({
  length: false,
  uppercase: false,
  lowercase: false,
  digit: false,
  special: false,
});

const strengthLabels = {
  length: 'Au moins 8 caractères',
  uppercase: 'Une majuscule',
  lowercase: 'Une minuscule',
  digit: 'Un chiffre',
  special: 'Un caractère spécial (!@#$%^&*)',
};

const isPasswordStrong = computed(
  () =>
    passwordChecks.value.length &&
    passwordChecks.value.uppercase &&
    passwordChecks.value.lowercase &&
    passwordChecks.value.digit &&
    passwordChecks.value.special
);

function validatePassword() {
  const p = newPassword.value;
  passwordChecks.value = {
    length: p.length >= 8,
    uppercase: /[A-Z]/.test(p),
    lowercase: /[a-z]/.test(p),
    digit: /[0-9]/.test(p),
    special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p),
  };
}

async function submitReset() {
  if (tokenInvalid.value) return;
  if (!isPasswordStrong.value) {
    errorMessage.value = 'Veuillez respecter tous les critères du mot de passe.';
    return;
  }
  if (newPassword.value !== confirmPassword.value) {
    errorMessage.value = 'Les mots de passe ne correspondent pas.';
    return;
  }
  errorMessage.value = '';
  loading.value = true;
  try {
    const token = route.params.token;
    await axios.put(`/api/password-reset/${token}/`, { new_password: newPassword.value });
    successMessage.value = 'ok';
    setTimeout(() => {
      let next = route.query.redirect;
      if (Array.isArray(next)) next = next[0];
      if (typeof next === 'string' && next.startsWith('/') && !next.startsWith('//')) {
        router.push({ path: '/connexion', query: { redirect: next } });
        return;
      }
      router.push('/connexion');
    }, 3000);
  } catch (error) {
    errorMessage.value = safeErrorMessage(error, 'Une erreur est survenue. Veuillez réessayer.');
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  const token = route.params.token;
  if (!token) {
    tokenInvalid.value = true;
    errorMessage.value = 'Lien de réinitialisation invalide.';
    return;
  }
  try {
    const { data } = await axios.get(`/api/password-reset/${token}/`);
    if (!data?.valid) {
      tokenInvalid.value = true;
      errorMessage.value = data?.error || 'Ce lien est invalide ou a expiré.';
    }
  } catch (error) {
    tokenInvalid.value = true;
    errorMessage.value = safeErrorMessage(
      error,
      'Ce lien de réinitialisation est invalide ou a expiré. Demandez un nouveau lien depuis la page « Mot de passe oublié ».'
    );
  }
});
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--surface, #f7f9fc);
  font-family: var(--font-body);
}

.auth-page--register .auth-page__wrap {
  width: 100%;
  max-width: 440px;
  flex: 1;
  justify-content: flex-start;
  padding: 0 1rem 2rem;
  margin: -1.5rem auto 0;
}

.auth-page__wrap {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.auth-card {
  background: #fff;
  border-radius: 1.25rem;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--line);
  padding: 2rem 1.75rem;
  width: 100%;
}

.auth-card--newpwd {
  border-radius: 16px;
  border-top: 3px solid var(--brand-gold);
}

.card-enter-active {
  transition: opacity 0.4s ease, transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.card-enter-from {
  opacity: 0;
  transform: translateY(16px);
}
.auth-card__header {
  text-align: center;
  padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;
  background-color: var(--brand-brown);
  color: #ffffff;
  border-bottom: 1px solid rgba(212, 160, 23, 0.2);
}
.auth-card__header--newpwd {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 2.25rem 1.5rem 2.75rem;
  background:
    linear-gradient(180deg, rgba(84, 57, 41, 0.08), transparent 40%),
    var(--brand-brown);
  border-bottom: none;
  border-radius: 0;
  box-sizing: border-box;
  text-align: center;
}

.auth-card__title--newpwd {
  font-family: var(--font-display);
  font-size: clamp(1.75rem, 1.35rem + 1.5vw, 2.5rem);
  font-weight: 600;
  color: #f7f1e8 !important;
  -webkit-text-fill-color: #f7f1e8;
  margin: 0 0 0.5rem 0;
  text-align: center;
  letter-spacing: -0.02em;
  line-height: 1.15;
}
.auth-card__subtitle--newpwd {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.88);
  margin: 0 auto;
  text-align: center;
  line-height: 1.55;
  max-width: 360px;
}
.auth-card__title {
  font-family: var(--font-display);
  font-size: var(--text-5xl);
  font-weight: var(--font-weight-bold);
  color: #d89c24;
  margin: 0 0 0.5rem 0;
  text-align: center;
  letter-spacing: var(--heading-letter-display);
  line-height: var(--line-tight);
}
.auth-card__subtitle {
  font-size: var(--text-prose);
  color: #ffffff;
  margin: 0;
  text-align: center;
  line-height: var(--line-normal);
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
}
.auth-card__subtitle--forgot {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.88);
  margin: 0 auto;
  text-align: center;
  line-height: 1.55;
  max-width: 360px;
}

.auth-form__group {
  margin-bottom: 1.25rem;
}
.auth-form__label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #334155;
  margin-bottom: 0.5rem;
}
.auth-form__input-wrap {
  position: relative;
}
.auth-form__icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  color: #94a3b8;
}
.auth-form__input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #0f172a;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.auth-form__input:focus {
  outline: none;
  border-color: #d89c24;
  box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.15);
}
.auth-form__input--with-btn {
  padding-right: 3rem;
}
.auth-form__toggle-pwd {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  padding: 0.25rem;
  color: #64748b;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 0.5rem;
}
.auth-form__toggle-pwd:hover {
  color: #0f172a;
  background: #f1f5f9;
}
.auth-form__strength {
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-radius: 0.75rem;
  border-left: 3px solid #e2e8f0;
}
.auth-form__strength-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: #64748b;
  margin-bottom: 0.25rem;
  transition: color 0.2s;
}
.auth-form__strength-item:last-child {
  margin-bottom: 0;
}
.auth-form__strength-item--ok {
  color: #059669;
}
.auth-form__strength-dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background: #cbd5e1;
}
.auth-form__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.75rem;
}
.auth-form__submit {
  width: 100%;
  padding: 0 var(--space-5);
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--brand-ink);
  background: var(--brand-gold);
  border: 1px solid transparent;
  border-radius: var(--radius-pill);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease), transform var(--dur-fast) var(--ease), box-shadow var(--dur-fast) var(--ease);
  min-height: 48px;
}
.auth-form__submit:hover:not(:disabled) {
  background: var(--brand-gold-light);
  color: var(--brand-ink);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}
.auth-form__submit:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.auth-form__spinner {
  display: inline-block;
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid color-mix(in srgb, var(--brand-ink) 25%, transparent);
  border-top-color: var(--brand-ink);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.auth-form__error--block {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 1rem;
  margin-top: 0.5rem;
  border-radius: 0.75rem;
}
.auth-form__success {
  text-align: center;
  padding: 1rem 0;
}
.auth-form__success-icon {
  margin: 0 auto 1rem;
  display: block;
}
.auth-form__success-msg {
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 0.5rem 0;
}
.auth-form__success-hint {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
@media (max-width: 480px) {
  .auth-card {
    padding: 1.5rem 1.25rem;
  }
  .auth-form__submit {
    min-height: 52px;
  }
}
</style>
